import { SearchResult } from "../types";
import { getSearchWorkerService } from "./searchWorkerService";
import { getOverpassWorkerService } from "./overpassWorkerService";
import { calculateDistanceMiles } from "../utils/geoUtils";
import debugx from "debugx";

const debug = debugx("bikedash:search");

type SearchDistance = "local" | "10mi" | "25mi" | "∞";

interface SearchState {
  query: string;
  results: SearchResult[];
  distance: SearchDistance;
  lastSearchPosition: { lat: number; lon: number } | null;
  isSearching: boolean;
  isWaiting: boolean; // Waiting during debounce period
}

class SearchService {
  private static instance: SearchService;
  private state: SearchState = {
    query: "",
    results: [],
    distance: "local",
    lastSearchPosition: null,
    isSearching: false,
    isWaiting: false,
  };

  private listeners: Set<(state: SearchState) => void> = new Set();
  private mapMoveTimer: NodeJS.Timeout | null = null;
  private searchDebounceTimer: NodeJS.Timeout | null = null;
  private cacheUpdateTimer: NodeJS.Timeout | null = null;
  private mapUnsubscribe: (() => void) | null = null;
  private cacheUpdateHandler: (() => void) | null = null;
  private currentSearchId = 0; // Track current search to prevent race conditions

  private constructor() {
    debug("SearchService initialized");
    this.setupMapListener();
    // Enable cache listener now that we have proper queuing
    this.setupCacheListener();
  }

  static getInstance(): SearchService {
    if (!SearchService.instance) {
      debug("Creating new SearchService instance");
      SearchService.instance = new SearchService();
    }
    return SearchService.instance;
  }

  private setupMapListener() {
    // Don't set up if we already have a listener
    if (this.mapUnsubscribe) {
      debug("Map listener already established, skipping");
      return;
    }

    // Set up persistent map movement listener
    const mapService = window.__bikedash_maps?.["main"];
    if (!mapService) {
      // Retry setup later if map isn't ready yet
      setTimeout(() => this.setupMapListener(), 1000);
      return;
    }

    this.mapUnsubscribe = mapService.onMoveEnd(() => {
      this.handleMapMove();
    });

    debug("Map movement listener established");
  }

  private setupCacheListener() {
    // Listen for cache updates
    this.cacheUpdateHandler = () => {
      // Only re-search if we're in local mode
      if (this.state.distance !== "local") {
        return;
      }

      // Clear existing timer
      if (this.cacheUpdateTimer) {
        clearTimeout(this.cacheUpdateTimer);
      }

      // Debounce for 500ms to avoid too many updates but stay responsive
      this.cacheUpdateTimer = setTimeout(() => {
        // Only search if there's a query and in local mode
        if (!this.state.query.trim() || this.state.distance !== "local") {
          return;
        }
        const mapService = window.__bikedash_maps?.["main"];
        const currentCenter = mapService?.getCenter();
        if (currentCenter) {
          debug("Re-running search after cache update");
          this.performSearch(
            this.state.query,
            this.state.distance,
            currentCenter,
          );
        }
      }, 500);
    };

    window.addEventListener("cacheUpdated", this.cacheUpdateHandler);
  }

  private handleMapMove() {
    // Skip if not in local mode
    if (this.state.distance !== "local") {
      return;
    }

    const mapService = window.__bikedash_maps?.["main"];
    if (!mapService) return;

    const currentCenter = mapService.getCenter();
    if (!currentCenter) return;

    // Only search if there's a query in local mode
    if (!this.state.query.trim()) {
      return;
    }

    // Check if map center changed significantly
    if (!this.state.lastSearchPosition) {
      // First time - do immediate search
      this.performSearch(this.state.query, this.state.distance, currentCenter);
      return;
    }

    const distance = calculateDistanceMiles(
      this.state.lastSearchPosition.lat,
      this.state.lastSearchPosition.lon,
      currentCenter.lat,
      currentCenter.lon,
    );

    if (distance > 2) {
      // Clear existing timer
      if (this.mapMoveTimer) {
        clearTimeout(this.mapMoveTimer);
      }

      // Debounce search
      this.mapMoveTimer = setTimeout(() => {
        this.performSearch(
          this.state.query,
          this.state.distance,
          currentCenter,
        );
      }, 1500); // Longer debounce for stability
    }
  }

  private async preloadCurrentTile(center: { lat: number; lon: number }) {
    const overpassWorker = getOverpassWorkerService();
    const GRID_SIZE = 0.2;
    const centerGridLat = Math.round(center.lat / GRID_SIZE) * GRID_SIZE;
    const centerGridLon = Math.round(center.lon / GRID_SIZE) * GRID_SIZE;

    // Check if tile is already cached
    const status = await overpassWorker.checkGridStatus(
      centerGridLat,
      centerGridLon,
    );
    if (status === "cached" || status === "fetching") {
      return;
    }

    debug("Preloading current tile at %.4f, %.4f", center.lat, center.lon);
    // Load the single tile if not already cached
    overpassWorker
      .searchLocalBikeFeatures(center.lat, center.lon, 1, false)
      .catch((err) => {
        debug("Failed to preload tile:", err);
      });
  }

  // Using shared geo utilities from geoUtils.ts

  async performSearch(
    query: string,
    distance: SearchDistance,
    center?: { lat: number; lon: number },
  ) {
    // Increment search ID to track this specific search
    const searchId = ++this.currentSearchId;
    debug(
      "Starting search #%d (query: '%s', distance: %s)",
      searchId,
      query,
      distance,
    );

    // Get center from map if not provided
    if (!center) {
      const mapService = window.__bikedash_maps?.["main"];
      center = mapService?.getCenter() || undefined;
      if (!center) {
        debug("No valid center for search");
        return;
      }
      debug("Got center from map: %.4f, %.4f", center.lat, center.lon);
    } else {
      debug("Using provided center: %.4f, %.4f", center.lat, center.lon);
    }

    // Check if this search is already obsolete
    if (searchId !== this.currentSearchId) {
      debug("Search #%d already obsolete, skipping", searchId);
      return;
    }

    // Ensure searching state is still true (should be set by setQuery already)
    this.state.isSearching = true;
    this.notifyListeners();

    // Dispatch search busy event
    window.dispatchEvent(new CustomEvent("searchBusyStart"));

    const workerService = getSearchWorkerService();

    try {
      // Also log the map center for comparison
      const mapService = window.__bikedash_maps?.["main"];
      const mapCenter = mapService?.getCenter();
      if (
        mapCenter &&
        (Math.abs(mapCenter.lat - center.lat) > 0.001 ||
          Math.abs(mapCenter.lon - center.lon) > 0.001)
      ) {
        debug("⚠️ Search center differs from map center!");
        debug("  Search at: %.4f, %.4f", center.lat, center.lon);
        debug("  Map at: %.4f, %.4f", mapCenter.lat, mapCenter.lon);
      }

      if (distance === "local") {
        debug("Performing local search at %.4f, %.4f", center.lat, center.lon);

        // Use overpass worker for heavy tile operations
        const overpassWorker = getOverpassWorkerService();
        const overpassResults = await overpassWorker.searchLocalBikeFeatures(
          center.lat,
          center.lon,
        );
        debug("Overpass worker fetched %d results", overpassResults.length);

        // ONLY use worker - no fallbacks!
        debug("Offloading %d results to worker", overpassResults.length);
        const formattedResults = await workerService.formatForDisplay(
          overpassResults,
          query || " ",
          center, // Pass search center so markers appear near user
        );

        // Filter and sort in worker too
        // IMPORTANT: Use the SAME center for filtering as we used for fetching!
        debug(
          "Sending filter request with center: %.4f, %.4f",
          center.lat,
          center.lon,
        );
        const displayResults = await workerService.filterAndSort(
          formattedResults,
          query,
          center, // Must be same position used for fetching
        );
        debug(
          "Worker processed %d results (from %d formatted, %d raw)",
          displayResults.length,
          formattedResults.length,
          overpassResults.length,
        );

        // Extra debug to verify we're getting nearest results
        if (displayResults.length > 0) {
          const first = displayResults[0];
          const last = displayResults[Math.min(49, displayResults.length - 1)];
          const firstDist = calculateDistanceMiles(
            parseFloat(first.lat),
            parseFloat(first.lon),
            center.lat,
            center.lon,
          );
          const lastDist = calculateDistanceMiles(
            parseFloat(last.lat),
            parseFloat(last.lon),
            center.lat,
            center.lon,
          );
          debug(
            "Distance check: first result %.3f mi, 50th result %.3f mi",
            firstDist,
            lastDist,
          );

          // Log first 3 results to see what we're getting
          console.log("🔍 Search center:", center);
          for (let i = 0; i < Math.min(3, displayResults.length); i++) {
            const result = displayResults[i];
            const dist = calculateDistanceMiles(
              parseFloat(result.lat),
              parseFloat(result.lon),
              center.lat,
              center.lon,
            );
            console.log(
              `  Result #${i + 1}: "${result.display_name}" at ${result.lat}, ${result.lon} (${dist.toFixed(3)} mi)`,
            );
          }
        }

        // Check if this is still the current search
        if (searchId !== this.currentSearchId) {
          debug(
            "Search #%d superseded by search #%d, discarding results",
            searchId,
            this.currentSearchId,
          );
          return;
        }

        // Update state
        this.state.results = displayResults.slice(0, 50); // Show nearest 50
        this.state.lastSearchPosition = center;

        // Notify listeners immediately so UI updates
        this.notifyListeners();

        // Update map markers after state is updated - get fresh reference
        const mapServiceLocal = window.__bikedash_maps?.["main"];
        if (mapServiceLocal) {
          debug(
            "Updating map with %d local search results",
            this.state.results.length,
          );
          mapServiceLocal.displaySearchResults(this.state.results);
        } else {
          debug.warn("Map service not available to display local results");
        }

        debug(
          "Local search completed with %d results",
          this.state.results.length,
        );
      } else {
        // Handle remote Nominatim search
        const bbox = this.getBoundingBox(center, distance);

        try {
          // Try worker for Nominatim search
          debug("Using worker for Nominatim search");
          const data = await workerService.searchNominatim({
            query,
            bbox: bbox, // Always provide bbox to prioritize nearby results
            bounded: distance !== "∞", // Only bound for non-infinite searches
          });

          this.state.results = data;
        } catch (workerError) {
          // Fallback to main thread
          debug.warn(
            "Worker failed for Nominatim, using main thread:",
            workerError,
          );
          const params: any = {
            q: query,
            format: "json",
            limit: "100",
            countrycodes: "us",
            polygon_geojson: "1",
            dedupe: "1",
            // Always provide viewbox to prioritize nearby results
            viewbox: `${bbox.west},${bbox.north},${bbox.east},${bbox.south}`,
          };

          // Only set bounded for non-infinite searches
          if (distance !== "∞") {
            params.bounded = "1";
          }

          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?${new URLSearchParams(params)}`,
          );
          const data = await response.json();
          this.state.results = data;
        }

        // Deduplicate results with same name and close proximity
        const dedupedResults: SearchResult[] = [];
        const seenNames = new Map<string, { lat: number; lon: number }>();

        for (const result of this.state.results) {
          const name = result.display_name.split(",")[0].trim(); // First part of display name
          const lat = parseFloat(result.lat);
          const lon = parseFloat(result.lon);

          // Check if we've seen this name before
          const existing = seenNames.get(name);
          if (existing) {
            // If the locations are close (< 1 mile), consider it a duplicate
            const dist = calculateDistanceMiles(
              existing.lat,
              existing.lon,
              lat,
              lon,
            );
            if (dist < 1.0) {
              debug("Skipping duplicate: %s (%.2f mi apart)", name, dist);
              continue; // Skip this duplicate
            }
          }

          // Not a duplicate, add it
          seenNames.set(name, { lat, lon });
          dedupedResults.push(result);
        }

        debug(
          "Deduplicated Nominatim results: %d -> %d",
          this.state.results.length,
          dedupedResults.length,
        );

        this.state.results = dedupedResults;

        // Sort by distance from center
        this.state.results.sort((a, b) => {
          const aDist = calculateDistanceMiles(
            parseFloat(a.lat),
            parseFloat(a.lon),
            center.lat,
            center.lon,
          );
          const bDist = calculateDistanceMiles(
            parseFloat(b.lat),
            parseFloat(b.lon),
            center.lat,
            center.lon,
          );
          return aDist - bDist; // Closer results first
        });

        debug(
          "Sorted %d Nominatim results by distance from %.4f, %.4f",
          this.state.results.length,
          center.lat,
          center.lon,
        );

        // Check if this is still the current search
        if (searchId !== this.currentSearchId) {
          debug(
            "Search #%d superseded by search #%d, discarding results",
            searchId,
            this.currentSearchId,
          );
          return;
        }

        this.state.lastSearchPosition = center;

        // Notify listeners immediately so UI updates
        this.notifyListeners();

        // Update map markers after state is updated - get fresh reference
        const mapServiceRemote = window.__bikedash_maps?.["main"];
        if (mapServiceRemote) {
          debug(
            "Updating map with %d remote search results",
            this.state.results.length,
          );
          mapServiceRemote.displaySearchResults(this.state.results);
        } else {
          debug.warn("Map service not available to display remote results");
        }

        debug(
          "Remote search completed with %d results",
          this.state.results.length,
        );
      }
    } catch (error) {
      debug.error("Search failed:", error);
      // Clear results on error
      this.state.results = [];
      this.state.isSearching = false;
      this.state.isWaiting = false;
      this.notifyListeners();

      // Clear map markers on error - get fresh reference
      const mapServiceError = window.__bikedash_maps?.["main"];
      if (mapServiceError) {
        mapServiceError.clearSearchResults();
      }
    } finally {
      this.state.isSearching = false;
      this.state.isWaiting = false;
      this.notifyListeners();

      // Dispatch search busy end event
      window.dispatchEvent(new CustomEvent("searchBusyEnd"));
    }
  }

  private filterAndSortResults(
    results: SearchResult[],
    query: string,
    center: { lat: number; lon: number },
  ): SearchResult[] {
    const searchTerm = query.toLowerCase().trim();

    // Filter results if there's a query
    let filtered = results;
    if (searchTerm) {
      filtered = results.filter((result) => {
        const displayNameMatch = result.display_name
          .toLowerCase()
          .includes(searchTerm);
        const tagsMatch =
          result.tags &&
          (result.tags.name?.toLowerCase().includes(searchTerm) ||
            result.tags.ref?.toLowerCase().includes(searchTerm) ||
            result.tags.description?.toLowerCase().includes(searchTerm));
        return displayNameMatch || tagsMatch;
      });
    }

    // Always sort by relevance and distance
    filtered.sort((a, b) => {
      const aName = a.display_name.toLowerCase();
      const bName = b.display_name.toLowerCase();
      const queryLower = searchTerm;

      // Calculate distances in miles
      const aDistance = calculateDistanceMiles(
        parseFloat(a.lat),
        parseFloat(a.lon),
        center.lat,
        center.lon,
      );
      const bDistance = calculateDistanceMiles(
        parseFloat(b.lat),
        parseFloat(b.lon),
        center.lat,
        center.lon,
      );

      // Only apply query-based sorting if there's a search term
      if (searchTerm) {
        // Exact match gets highest priority
        if (aName === queryLower) return -1;
        if (bName === queryLower) return 1;

        // Starts with query
        if (aName.startsWith(queryLower) && !bName.startsWith(queryLower))
          return -1;
        if (bName.startsWith(queryLower) && !aName.startsWith(queryLower))
          return 1;
      }

      // Score based on importance and distance (closer is better)
      const aScore = a.importance - aDistance * 0.1; // 0.1 penalty per mile
      const bScore = b.importance - bDistance * 0.1;

      return bScore - aScore;
    });

    return filtered;
  }

  private getBoundingBox(
    center: { lat: number; lon: number },
    distance: SearchDistance,
  ) {
    const miles = distance === "10mi" ? 10 : distance === "25mi" ? 25 : 50;
    const latDelta = miles / 69;
    const lonDelta = miles / (69 * Math.cos((center.lat * Math.PI) / 180));

    return {
      north: center.lat + latDelta,
      south: center.lat - latDelta,
      east: center.lon + lonDelta,
      west: center.lon - lonDelta,
    };
  }

  // Public API
  setQuery(query: string) {
    this.state.query = query;

    // Debounce search
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    if (query.trim()) {
      // Clear old results and show waiting state immediately
      this.state.results = [];
      this.state.isWaiting = true;
      this.state.isSearching = false;

      // Clear map markers for old results
      const mapService = window.__bikedash_maps?.["main"];
      if (mapService) {
        mapService.clearSearchResults();
      }

      this.notifyListeners();

      // Shorter debounce for local searches (200ms) vs remote searches (2000ms)
      const debounceTime = this.state.distance === "local" ? 200 : 2000;
      this.searchDebounceTimer = setTimeout(() => {
        // Clear waiting state and start search
        this.state.isWaiting = false;
        this.performSearch(query, this.state.distance);
      }, debounceTime);
    } else {
      // Clear results if query is empty
      this.state.isWaiting = false;
      this.clearResults();
      this.notifyListeners();
    }
  }

  setDistance(distance: SearchDistance) {
    this.state.distance = distance;

    // Get current position for the search
    const mapService = window.__bikedash_maps?.["main"];
    const currentCenter = mapService?.getCenter();

    if (currentCenter) {
      // Set position before search to prevent duplicate search in handleMapMove
      this.state.lastSearchPosition = currentCenter;

      if (this.state.query.trim()) {
        this.performSearch(this.state.query, distance, currentCenter);
      }
    } else if (this.state.query.trim()) {
      // Fallback if no center available
      this.state.lastSearchPosition = null;
      this.performSearch(this.state.query, distance);
    }

    this.notifyListeners();
  }

  clearResults() {
    this.state.results = [];
    this.state.isWaiting = false;
    this.state.isSearching = false;

    const mapService = window.__bikedash_maps?.["main"];
    if (mapService) {
      mapService.clearSearchResults();
    }

    this.notifyListeners();
  }

  clearQuery() {
    this.state.query = "";
    this.clearResults();
  }

  getState(): SearchState {
    return { ...this.state };
  }

  hasActiveSearch(): boolean {
    return this.state.query.trim().length > 0;
  }

  subscribe(listener: (state: SearchState) => void): () => void {
    this.listeners.add(listener);
    debug("Added listener, now have %d listeners", this.listeners.size);

    // Call immediately with current state
    listener(this.getState());

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
      debug("Removed listener, now have %d listeners", this.listeners.size);
    };
  }

  triggerTilePreload() {
    if (this.state.distance === "local") {
      const mapService = window.__bikedash_maps?.["main"];
      const currentCenter = mapService?.getCenter();
      if (currentCenter) {
        debug(
          "Triggering tile preload at %.4f, %.4f",
          currentCenter.lat,
          currentCenter.lon,
        );
        this.preloadCurrentTile(currentCenter);
      }
    }
  }

  private notifyListeners() {
    const state = this.getState();
    debug(
      "Notifying %d listeners with %d results",
      this.listeners.size,
      state.results.length,
    );
    this.listeners.forEach((listener) => {
      try {
        listener(state);
      } catch (error) {
        debug.error("Listener error:", error);
      }
    });
  }

  destroy() {
    if (this.mapMoveTimer) {
      clearTimeout(this.mapMoveTimer);
    }
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    if (this.cacheUpdateTimer) {
      clearTimeout(this.cacheUpdateTimer);
    }
    if (this.mapUnsubscribe) {
      this.mapUnsubscribe();
    }
    // Remove cache update listener
    if (this.cacheUpdateHandler) {
      window.removeEventListener("cacheUpdated", this.cacheUpdateHandler);
    }
    this.listeners.clear();
  }
}

// Export singleton getter
export function getSearchService(): SearchService {
  return SearchService.getInstance();
}

// Export SearchService type
export { SearchService };
